import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { PlayerSubscribeManagers } from "../../redux/auth-reducer/auth-service";
import { readApiMessage } from "../../utils/helper/helper";
import { confirmButtonLabel, confirmIntro } from "../../utils/helper/managers";
import { ManagerIdentity } from "./ManagerIdentity";

/**
 * "Confirm Subscription Request" (#133): the only place a player sends subscription requests, for
 * one manager (a row's or the widget's Subscribe) or several (the bulk Subscribe). Nothing is sent
 * until Send Request(s).
 *
 * onSent receives the server's reply ({status, message, sentCount, skippedCount}) whenever the
 * server answered - including when every manager was skipped, because their statuses changed and
 * the list has to be reloaded either way. With no reply the dialog stays open (apiClient has
 * already told the player), so Send Request(s) is its own retry.
 *
 * @param {{show: boolean, managers: object[], onCancel: () => void, onSent: (result: object) => void}} props
 */
export const SubscribeConfirmModal = ({ show, managers, onCancel, onSent }) => {
  const [busy, setBusy] = useState(false);
  const list = managers || [];

  const send = async () => {
    setBusy(true);
    const res = await PlayerSubscribeManagers(list.map((manager) => manager.managerId));
    setBusy(false);

    if (typeof res?.data?.sentCount === "number") {
      onSent(res.data);
      return;
    }

    // A fault reply carries a readable message and gets no generic toast from apiClient.
    if (res) toast.error(readApiMessage(res));
  };

  return (
    <Modal show={show} centered onHide={busy ? undefined : onCancel}>
      <Modal.Header className="promo-code-header promo-close" closeButton={!busy}>
        <Modal.Title>
          <b>Confirm Subscription Request</b>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{confirmIntro(list.length)}</p>
        <div className="manager-confirm-list">
          {list.map((manager) => (
            <ManagerIdentity key={manager.managerId} manager={manager} compact />
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={send} disabled={busy || list.length === 0}>
          {busy ? "Sending..." : confirmButtonLabel(list.length)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
